import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 * Uses optimistic UI updates for instant responsiveness.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  // Debounce server sync, not UI updates
  #debouncedServerSync = debounce(this.#syncToServer, 300).bind(this);
  #pendingUpdates = new Map(); // variantId (string) -> quantity (number)
  #isSyncing = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    // Listen for quantity changes WITHOUT debounce for instant UI
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent - updates UI instantly, debounces server sync
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number (for optimistic remove only)
    if (!line) return;

    // Get variantId from the quantity-selector-component element.
    // Keying by variantId (not line) is critical: line numbers shift after removals,
    // causing stale-line crashes when multiple items are changed rapidly.
    const variantId = event.target instanceof HTMLElement ? event.target.dataset.variantId : null;
    const key = variantId || String(line); // variantId always preferred; String(line) is a safe fallback

    // Store pending update keyed by variantId — last write wins (Map deduplicates)
    this.#pendingUpdates.set(key, quantity);

    if (quantity === 0) {
      // Optimistically hide the row immediately
      this.#optimisticRemove(line);
    }

    // Debounce the actual server sync
    this.#debouncedServerSync();
  };

  /**
   * Optimistically removes a row from the UI
   * @param {number} line - The line item index.
   */
  #optimisticRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Syncs all pending updates to the server
   */
  #syncToServer() {
    if (this.#pendingUpdates.size === 0 || this.#isSyncing) return;

    this.#isSyncing = true;
    
    // Get all pending updates and clear the queue
    const updates = new Map(this.#pendingUpdates);
    this.#pendingUpdates.clear();

    // Process updates sequentially to maintain order
    this.#processUpdates(Array.from(updates.entries()));
  }

  /**
   * Process all pending updates as a single atomic /cart/update.js request.
   *
   * Why /cart/update.js instead of sequential /cart/change.js?
   * /cart/change.js addresses items by 1-indexed line position. When you remove
   * line 1, every subsequent line shifts down — so a pending update for "line 2"
   * now points to what was line 3. Rapid removes + quantity changes therefore land
   * on the wrong items. /cart/update.js addresses items by variantId, which never
   * changes regardless of removal order, and processes everything in one request.
   *
   * @param {Array<[string, number]>} updates - Array of [variantId, quantity] pairs
   */
  async #processUpdates(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('batch-update:user-action');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    try {
      // Single atomic request — all updates at once, no stale line numbers
      const updatesObj = Object.fromEntries(updates);

      const body = JSON.stringify({
        updates: updatesObj,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      const response = await fetch('/cart/update.js', fetchConfig('json', { body }));
      const responseText = await response.text();
      const parsedResponse = JSON.parse(responseText);

      if (parsedResponse.errors) {
        // Re-render from server to restore correct state
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        return;
      }

      this.#updateQuantitySelectors(parsedResponse);

      const sectionHtml = parsedResponse.sections?.[this.sectionId];
      const newSectionHTML = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponse, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponse.sections,
        })
      );

      if (sectionHtml) {
        morphSection(this.sectionId, sectionHtml);
        this.#updateCartQuantitySelectorButtonStates();
      }
    } catch (error) {
      console.error('Cart update error:', error);
    } finally {
      this.#isSyncing = false;
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);

      // If more updates came in while syncing, process them
      if (this.#pendingUpdates.size > 0) {
        this.#debouncedServerSync();
      }
    }
  }

  /**
   * Handles the line item removal (legacy method for compatibility).
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    const key = row?.dataset.variantId || String(line);
    this.#pendingUpdates.set(key, 0);
    this.#optimisticRemove(line);
    this.#debouncedServerSync();
  }

  /**
   * Updates the quantity (legacy method for compatibility).
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const row = this.refs.cartItemRows?.[config.line - 1];
    const key = row?.dataset.variantId || String(config.line);
    this.#pendingUpdates.set(key, config.quantity);
    if (config.quantity === 0) {
      this.#optimisticRemove(config.line);
    }
    this.#debouncedServerSync();
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
